import type { ConditionModel } from '@ctw/shared/api/fhir/models/condition';
import type { DiagnosticReportModel } from '@ctw/shared/api/fhir/models/diagnostic-report';
import type { DocumentModel } from '@ctw/shared/api/fhir/models/document';
import type { FHIRModel } from '@ctw/shared/api/fhir/models/fhir-model';
import type { MedicationStatementModel } from '@ctw/shared/api/fhir/models/medication-statement';
import type { ObservationModel } from '@ctw/shared/api/fhir/models/observation';
import type { RiskGapEvidenceReference, RiskGapModel } from '@ctw/shared/api/fhir/models/risk-gap';
import { tw } from '@ctw/shared/utils/tailwind';
import { SYSTEM_URLS } from '@zeus-health/types';
import type { Resource } from 'fhir/r4';
import { capitalize } from 'lodash-es';

interface ClinicalEvidenceCardProps {
  reference: RiskGapEvidenceReference;
  model?: FHIRModel<Resource>;
  onClick?: () => void;
}

interface ClinicalEvidenceCardContent {
  title: string;
  primary: string;
  secondary?: string;
  date?: string;
  quote?: string;
}

type SupportedEvidenceResourceType =
  | 'Condition'
  | 'Observation'
  | 'DiagnosticReport'
  | 'DocumentReference'
  | 'MedicationStatement'
  | 'MeasureReport';

type SupportedEvidenceModel =
  | ConditionModel
  | ObservationModel
  | DiagnosticReportModel
  | DocumentModel
  | MedicationStatementModel
  | RiskGapModel;

interface ClinicalEvidenceHandler<TModel extends SupportedEvidenceModel> {
  resourceType: SupportedEvidenceResourceType;
  toCardContent: (model: TModel | undefined, quote?: string) => ClinicalEvidenceCardContent;
}

const CLINICAL_EVIDENCE_HANDLERS: Record<
  SupportedEvidenceResourceType,
  ClinicalEvidenceHandler<SupportedEvidenceModel>
> = {
  Condition: {
    resourceType: 'Condition',
    toCardContent: (model) => {
      const condition = model as ConditionModel | undefined;
      if (condition?.isSummaryResource) {
        return {
          title: 'Condition',
          primary: condition.display || 'Unknown',
          secondary: condition.onset ? `Onset ${condition.onset}` : undefined,
          date: condition.recordedDate ? `Last recorded ${condition.recordedDate}` : undefined,
        };
      }
      return {
        title: condition?.isEncounterDiagnosis ? 'Encounter Diagnosis' : 'Condition',
        primary: condition?.icd10CMEnrichments[0]?.code || condition?.display || 'Unknown',
        secondary: condition?.display,
        date: condition?.recordedDate ? `Recorded ${condition.recordedDate}` : undefined,
      };
    },
  },
  Observation: {
    resourceType: 'Observation',
    toCardContent: (model) => {
      const observation = model as ObservationModel | undefined;
      return {
        title: 'Observation',
        primary: observation?.valueWithUnits || 'Unknown result',
        secondary: observation?.title || 'Observation',
        date: observation?.dateDisplay ? `Effective date ${observation.dateDisplay}` : undefined,
      };
    },
  },
  DiagnosticReport: {
    resourceType: 'DiagnosticReport',
    toCardContent: (model) => {
      const diagnosticReport = model as DiagnosticReportModel | undefined;
      return {
        title: 'Diagnostic Report',
        primary: diagnosticReport?.displayName || 'Diagnostic report',
        secondary:
          diagnosticReport?.performer ||
          diagnosticReport?.patientOrganizationName ||
          diagnosticReport?.numResultsDisplay ||
          'Diagnostic report',
        date: diagnosticReport?.dateDisplay
          ? `Effective date ${diagnosticReport.dateDisplay}`
          : undefined,
      };
    },
  },
  DocumentReference: {
    resourceType: 'DocumentReference',
    toCardContent: (model, quote) => {
      const document = model as DocumentModel | undefined;
      let date: string | undefined;
      if (document?.encounterDate && document?.dateCreated) {
        date = `Encounter ${document.encounterDate}, Retrieved ${document.dateCreated}`;
      }
      if (document?.encounterDate) {
        date = `Encounter ${document.encounterDate}`;
      }
      if (document?.dateCreated) {
        date = `Retrieved ${document.dateCreated}`;
      }
      return {
        title: 'Document',
        primary: document?.typeDisplay || 'Document',
        date,
        quote,
      };
    },
  },
  MedicationStatement: {
    resourceType: 'MedicationStatement',
    toCardContent: (model) => {
      const medication = model as MedicationStatementModel | undefined;
      return {
        title: 'Medication',
        primary: medication?.display || 'Unknown medication',
        date: medication?.lastPrescribedDate
          ? `Last prescribed ${medication.lastPrescribedDate}`
          : undefined,
      };
    },
  },
  MeasureReport: {
    resourceType: 'MeasureReport',
    toCardContent: (model) => {
      const riskGap = model as RiskGapModel | undefined;
      const isPayerGap =
        riskGap?.resource.meta?.profile?.includes(
          SYSTEM_URLS.PROFILE.PAYER_RISK_GAP_MEASURE_REPORT,
        ) ?? false;

      if (!isPayerGap) {
        return {
          title: 'Risk Gap',
          primary: 'Unsupported evidence',
        };
      }

      const icd10Codes = riskGap?.icd10Codes?.map((c) => c.code).filter(Boolean) ?? [];
      const icd10Display = icd10Codes.length > 0 ? `ICD-10 ${icd10Codes.join(', ')}` : '';
      const hccDisplay =
        riskGap?.hccCode && riskGap.hccCode !== 'N/A' ? `HCC ${riskGap.hccCode}` : '';
      return {
        title: 'Payer Risk Gap',
        primary: [hccDisplay, icd10Display].filter(Boolean).join(' · '),
        secondary: capitalize(
          riskGap?.resource.extension?.find(
            (ext) => ext.url === SYSTEM_URLS.EXTENSIONS.PAYER_RISK_GAP.STATUS,
          )?.valueString,
        ),
        date: riskGap?.period ? `Period ${riskGap.period}` : undefined,
      };
    },
  },
};

export const ClinicalEvidenceCard = ({ reference, model, onClick }: ClinicalEvidenceCardProps) => {
  const handler =
    CLINICAL_EVIDENCE_HANDLERS[reference.resourceType as SupportedEvidenceResourceType];

  const content = handler
    ? handler.toCardContent(model as SupportedEvidenceModel | undefined, reference.display)
    : {
        title: reference.resourceType,
        primary: reference.resourceId,
        secondary: reference.resourceType,
      };

  return (
    <button
      type="button"
      onClick={onClick}
      className={tw`group relative flex cursor-pointer flex-col rounded-lg border border-border bg-card p-4 text-left transition-all hover:border-border/80 hover:shadow-sm`}
    >
      <div className={tw`mb-2 flex min-h-[16px] items-center justify-between`}>
        <div
          className={tw`font-semibold text-meta-main text-muted-foreground text-xs uppercase tracking-wider`}
        >
          {content.title}
        </div>
      </div>
      <div className={tw`flex flex-1 flex-col`}>
        <div className={tw`break-words font-semibold text-foreground text-lg leading-tight`}>
          {content.primary}
        </div>
        {content.secondary && (
          <div className={tw`mt-0.5 break-words text-muted-foreground text-sm`}>
            {content.secondary}
          </div>
        )}
        {content.quote && (
          <div
            className={tw`mt-2 border-muted-foreground/30 border-l-2 pl-2 text-muted-foreground text-sm italic`}
          >
            {content.quote}
          </div>
        )}
        <div className={tw`flex-1`} />
        {content.date && (
          <div className={tw`mt-auto pt-2 text-content-subtle text-slate-500 text-xs`}>
            {content.date}
          </div>
        )}
      </div>
    </button>
  );
};
